import React from 'react';
import { Building2, MapPin } from 'lucide-react';
import { RankNum, occColor } from './Podium';

/**
 * The pieces every ranking page is built from — דירוגים, טבלאות, סופי שבוע
 * and סניפים (§12.11, §12.13): a ranking card's head (title, ‹ period ›, its
 * own total on the left), the chips, the phone row, change and occupancy in
 * their colours. One definition, so the four pages cannot drift apart.
 */

export const PAGE_MAX = 1500;
export const fmt = (v) => (v || 0).toLocaleString();
export const card = { background: 'var(--surf)', borderRadius: 16, boxShadow: 'var(--shadow)', padding: 20, minWidth: 0 };
// Numbers, dates and "16+" keep reading left to right inside Hebrew.
export const ltr = (t) => <span dir="ltr" style={{ unicodeBidi: 'isolate', whiteSpace: 'nowrap' }}>{t}</span>;

export function Chip({ label, active, count, onClick, small, data }) {
  return (
    <button onClick={onClick} data-chip={data || (typeof label === 'string' ? label : undefined)} style={{
      padding: small ? '5px 11px' : '7px 14px', borderRadius: 20, cursor: 'pointer', whiteSpace: 'nowrap',
      fontFamily: 'Heebo, sans-serif', fontSize: small ? 12 : 13, fontWeight: 700,
      border: active ? '1px solid var(--accent)' : '1px solid var(--brd)',
      background: active ? 'var(--accent)' : 'var(--surf)', color: active ? '#fff' : 'var(--muted)',
    }}>{label}{count != null && <span style={{ opacity: 0.75, fontWeight: 600 }}> · {count}</span>}</button>
  );
}

export const ChipRow = ({ children, small }) => (
  <div style={{ display: 'flex', gap: small ? 6 : 8, flexWrap: 'wrap', alignItems: 'center' }}>{children}</div>
);

export function NewBadge({ size = 11 }) {
  return <span style={{ fontSize: size, fontWeight: 700, color: 'var(--accent)', background: 'var(--accent-bg)',
                        padding: '1px 7px', borderRadius: 10, whiteSpace: 'nowrap' }}>חדש</span>;
}

// ↑12% / ↓8%, or "חדש" when there is nothing to compare with.
export function Change({ curr, prev, size = 12 }) {
  if (!prev) return <NewBadge size={Math.min(size, 11)} />;
  const pct = Math.round((curr - prev) * 100 / prev);
  const up = pct >= 0;
  return (
    <span dir="ltr" data-change style={{ unicodeBidi: 'isolate', fontSize: size, fontWeight: 700, whiteSpace: 'nowrap',
                                         color: up ? 'var(--ok)' : 'var(--danger)' }}>
      {up ? '↑' : '↓'}{Math.abs(pct)}%
    </span>
  );
}

// Occupancy in colour: under 10% red, under 18% amber, 18% and up green.
// `plain` for a period still selling (today, a presale), which would read red
// for no reason — as on "מה חדש".
export const Occ = ({ v, plain }) => {
  const p = Math.round(v || 0);
  return <span data-occ style={{ fontWeight: 700, color: plain ? 'var(--muted)' : occColor(p) }}>{p}%</span>;
};

// A share; under one percent but not nothing reads "<1%".
export const sharePct = (share, tickets) => (share > 0 ? `${share}%` : tickets > 0 ? '<1%' : '0%');
export const Share = ({ share, tickets }) => (
  <span dir="ltr" style={{ unicodeBidi: 'isolate', color: 'var(--muted)' }}>{sharePct(share, tickets)}</span>
);

// ‹ earlier · later › — time runs left to right, as in every date stepper here.
// Without onPrev / onNext the label stands alone (all time, a custom range).
export function Stepper({ label, onPrev, onNext, nextDisabled, prevDisabled, big }) {
  const btn = (dis) => ({ width: 28, height: 28, borderRadius: 8, border: '1px solid var(--brd)', background: 'var(--surf)',
                         color: dis ? 'var(--brd)' : 'var(--muted)', cursor: dis ? 'default' : 'pointer', fontSize: 16, lineHeight: 1, padding: 0 });
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, direction: 'ltr' }}>
      {onPrev && <button aria-label="התקופה הקודמת" data-step="prev" disabled={prevDisabled} style={btn(prevDisabled)}
                         onClick={() => !prevDisabled && onPrev()}>‹</button>}
      <span data-period-label style={{ fontSize: big ? 22 : 15, fontWeight: big ? 800 : 700, color: big ? 'var(--txt)' : 'var(--muted)',
                                       whiteSpace: 'nowrap', direction: 'rtl' }}>{label}</span>
      {onNext && <button aria-label="התקופה הבאה" data-step="next" disabled={nextDisabled} style={btn(nextDisabled)}
                         onClick={() => !nextDisabled && onNext()}>›</button>}
    </div>
  );
}

// A ranking card's head: title and stepper on the right, its own total on the
// left (under the title on a phone). `line` is the row under the total.
export function Head({ title, nav, tag, total, line, mob }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px 16px', flexWrap: 'wrap', marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px 10px', flexWrap: 'wrap', minWidth: 0 }}>
        {title && <div data-rank-title style={{ fontSize: mob ? 18 : 22, fontWeight: 800, color: 'var(--txt)' }}>{title}</div>}
        {nav}
        {tag}
      </div>
      <div style={{ textAlign: mob ? 'right' : 'left', width: mob ? '100%' : 'auto', marginInlineStart: mob ? 0 : 'auto' }}>
        <div data-rank-total style={{ fontSize: mob ? 24 : 30, fontWeight: 900, color: 'var(--accent)', letterSpacing: -1, lineHeight: 1 }}>{fmt(total)}</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6, flexWrap: 'wrap', fontSize: 12, color: 'var(--faint)',
                      justifyContent: mob ? 'flex-start' : 'flex-end', whiteSpace: 'nowrap' }}>{line}</div>
      </div>
    </div>
  );
}

export function PresaleTag() {
  return <span data-presale style={{ fontSize: 10, fontWeight: 700, color: 'var(--presale)', background: 'var(--presale-bg)',
                                     padding: '3px 8px', borderRadius: 6, whiteSpace: 'nowrap' }}>מכירה מוקדמת</span>;
}

export const SubTitle = ({ children, first }) => (
  <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--muted)', margin: first ? '2px 0 10px' : '18px 0 6px' }}>{children}</div>
);

// A phone row: rank, picture, name with a line under it, and the figures
// stacked on the left — the "מה חדש" row.
export function PhoneRow({ i, poster, name, sub, tickets, line1, line2, onClick, rank = true }) {
  return (
    <div data-phone-row onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0',
                                                   borderBottom: '1px solid var(--brd)', cursor: onClick ? 'pointer' : 'default' }}>
      {rank && <RankNum i={i} />}
      {poster}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div data-row-name style={{ fontSize: 13, fontWeight: 700, color: 'var(--txt)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
        {sub && <div style={{ fontSize: 10.5, color: 'var(--faint)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 1 }}>{sub}</div>}
      </div>
      <div style={{ textAlign: 'center', flexShrink: 0, whiteSpace: 'nowrap' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)' }}>{fmt(tickets)}</div>
        {line1 && <div style={{ fontSize: 10, color: 'var(--faint)' }}>{line1}</div>}
        {line2 && <div style={{ fontSize: 10, color: 'var(--faint)' }}>{line2}</div>}
      </div>
    </div>
  );
}

// A chain or a branch has no poster: an icon tile stands in.
export function PlaceTile({ kind, size = 'sm' }) {
  const Icon = kind === 'chain' ? Building2 : MapPin;
  const s = { sm: 36, md: 52, xl: 64 }[size] || 36;
  return (
    <div data-place-tile={kind} style={{ width: s, height: s, borderRadius: s > 40 ? 14 : 9, background: 'var(--accent-bg)',
                                         color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Icon size={Math.round(s * 0.48)} strokeWidth={1.8} />
    </div>
  );
}

export function CustomRange({ value, onChange }) {
  const dark = document.documentElement.dataset.theme === 'dark';
  const input = { background: 'var(--surf)', border: '1px solid var(--brd)', borderRadius: 8, padding: '5px 8px',
                  color: 'var(--txt)', fontSize: 13, fontFamily: 'Heebo, sans-serif', colorScheme: dark ? 'dark' : 'light' };
  return (
    <div data-custom-range style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', fontSize: 12.5, color: 'var(--muted)' }}>
      מ-<input type="date" value={value.start} max={value.end} style={input}
               onChange={(e) => e.target.value && onChange({ ...value, start: e.target.value })} />
      עד<input type="date" value={value.end} min={value.start} style={input}
               onChange={(e) => e.target.value && onChange({ ...value, end: e.target.value })} />
    </div>
  );
}

// Whether a change against the previous period is a fair figure (§12.13):
// never for a year or all time (collection began in 2026, so a year has
// nothing to stand against), never for a period still ahead, and for a day,
// a week, a month or a custom range only once it has ended — while it is
// still selling it always reads as a fall. A weekend shows it while running,
// as on "מה חדש".
export function fairChange(kind, range, today) {
  if (!range || kind === 'year' || kind === 'all') return false;
  if (range.start > today) return false;
  if (kind === 'weekend') return true;
  return range.end < today;
}
